import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

/**
 * ANSI B36.10 nominal pipe size (NPS) → outer diameter (OD) table, used to
 * auto-derive NPS from a component's modelled outer diameter in the FFS
 * pre-planning classification form (`nps` is one of the Fig 4.5 Type-A piping
 * facts in `ffsComponentClassification.ts`).
 *
 * NPS is in inches (fractional sizes as decimals, e.g. NPS 1/8 = 0.125). OD is
 * in inches. Declaration order is smallest-to-largest and is NOT relied upon
 * by the lookup (which scans the whole table), only kept for readability.
 */
export const ANSI_B36_10_NPS_OD: ReadonlyArray<{
  nps: number
  odInches: number
}> = [
  { nps: 0.125, odInches: 0.405 },
  { nps: 0.25, odInches: 0.54 },
  { nps: 0.375, odInches: 0.675 },
  { nps: 0.5, odInches: 0.84 },
  { nps: 0.75, odInches: 1.05 },
  { nps: 1, odInches: 1.315 },
  { nps: 1.25, odInches: 1.66 },
  { nps: 1.5, odInches: 1.9 },
  { nps: 2, odInches: 2.375 },
  { nps: 2.5, odInches: 2.875 },
  { nps: 3, odInches: 3.5 },
  { nps: 3.5, odInches: 4.0 },
  { nps: 4, odInches: 4.5 },
  { nps: 5, odInches: 5.563 },
  { nps: 6, odInches: 6.625 },
  { nps: 8, odInches: 8.625 },
  { nps: 10, odInches: 10.75 },
  { nps: 12, odInches: 12.75 },
  { nps: 14, odInches: 14.0 },
  { nps: 16, odInches: 16.0 },
  { nps: 18, odInches: 18.0 },
  { nps: 20, odInches: 20.0 },
  { nps: 22, odInches: 22.0 },
  { nps: 24, odInches: 24.0 },
  { nps: 30, odInches: 30.0 },
  { nps: 32, odInches: 32.0 },
  { nps: 34, odInches: 34.0 },
  { nps: 36, odInches: 36.0 },
  { nps: 42, odInches: 42.0 },
]

/**
 * Relative match tolerance for snapping a measured OD to a standard NPS OD:
 * the nearest table OD is accepted only when the absolute difference is
 * within this fraction of that standard OD (2%). Manufacturing/measurement
 * noise on the modelled OD is expected to be well under this; a bigger gap
 * means the component likely isn't standard pipe (or D wasn't captured), so
 * we report "no confident match" rather than guess.
 */
export const NPS_OD_MATCH_TOLERANCE = 0.02

/**
 * Nearest-standard-OD lookup. Returns `null` when `odInches` is unusable
 * (non-finite or non-positive) or when the nearest table row is still farther
 * than `NPS_OD_MATCH_TOLERANCE` (relative to that row's OD) from `odInches` --
 * i.e. no confident match.
 */
export function npsFromOuterDiameterInches(odInches: number): number | null {
  if (!Number.isFinite(odInches) || odInches <= 0) {
    return null
  }

  let nearest = ANSI_B36_10_NPS_OD[0]
  let nearestDiff = Math.abs(odInches - nearest.odInches)
  for (const row of ANSI_B36_10_NPS_OD) {
    const diff = Math.abs(odInches - row.odInches)
    if (diff < nearestDiff) {
      nearest = row
      nearestDiff = diff
    }
  }

  return nearestDiff <= NPS_OD_MATCH_TOLERANCE * nearest.odInches
    ? nearest.nps
    : null
}

/**
 * Same lookup, but starting from the asset-model outer diameter in METERS
 * (the unit `ClassificationFacts.D` is stored in) rather than inches. `null`
 * when `dMeters` is absent, non-finite, or non-positive.
 */
export function npsFromOuterDiameterMeters(
  dMeters: number | undefined
): number | null {
  if (dMeters === undefined || !Number.isFinite(dMeters) || dMeters <= 0) {
    return null
  }
  const odInches = GeckoMeasurement.CreateFromValue(
    dMeters,
    GeckoMeasurementUnit.Meters
  ).convertTo(GeckoMeasurementUnit.Inches).num
  return npsFromOuterDiameterInches(odInches)
}

/**
 * The caption shown under the NPS input: what was auto-derived from the OD,
 * and whether the engineer overrode it. PURE (no React) so the wording is
 * unit-tested rather than assembled inline in JSX -- mirrors the intent of
 * `describeDerivedCorrosion` in `ffsClassificationContent.ts`.
 *
 * Returns `null` when there is nothing to say: no OD-derived NPS to report
 * (the input stays a plain manual entry).
 */
export function describeDerivedNps({
  derivedNps,
  odInches,
  overridden,
}: {
  derivedNps: number | null
  odInches: number | null
  overridden: boolean
}): string | null {
  if (derivedNps === null) {
    return null
  }

  const measuredPhrase =
    odInches !== null && Number.isFinite(odInches)
      ? `the modelled outer diameter (≈ ${odInches.toFixed(3)} in)`
      : 'the modelled outer diameter'

  const method = `matching ${measuredPhrase} to the nearest standard ANSI B36.10 pipe size`

  return overridden
    ? `Overridden. NPS ${derivedNps} was auto-derived by ${method}.`
    : `Auto-derived NPS ${derivedNps} by ${method}.`
}
